import userManager from '../managers/UserManager';

import { getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from '../utils/session';

import { registerClass } from '../SatCoreRegistry';

import productManager from '../managers/ProductManager';

export default class CourseNavigationService {
  static getCurrentCourseLocation = async (classroomId, courseId = null) => {
    if (!productManager.isFromProduct) {
      const key = this.getLastCourseLocationSessionStorageKey(classroomId, courseId);
      return JSON.parse(getSessionStorageItem(key) || null);
    }
  }

  static saveCurrentCourseLocation = async (
    classroomId,
    courseId,
    courseElementId = ''
  ) => {
    if (userManager.isTeacher && !productManager.isFromProduct) {
      const lastLocationData = {
        classroomId,
        courseElementId,
        courseId
      };

      const key = this.getLastCourseLocationSessionStorageKey(classroomId, courseId);
      setSessionStorageItem(key, JSON.stringify(lastLocationData));
    }
  }

  static removeClassroomCourseLocation = async (classroomId, courseId) => {
    if (userManager.isTeacher && !productManager.isFromProduct) {
      const key = this.getLastCourseLocationSessionStorageKey(classroomId, courseId);

      const lastLocationDataStr = getSessionStorageItem(key);
      if (lastLocationDataStr) {
        const lastLocationDataJson = JSON.parse(lastLocationDataStr);
        if (lastLocationDataJson.courseId === courseId) {
          removeSessionStorageItem(key);
        }
      }
    }
  }

  static getLastCourseLocationSessionStorageKey = (classroomId, _courseId) => {
    if (classroomId !== 'FROM_TEACHER_PRODUCTS_NAV' && !productManager.isFromProduct) {
      return `c2c_lastCourseLocation_${classroomId}`;
    }
    // TODO remove
    // if (classroomId === 'FROM_TEACHER_PRODUCTS_NAV') {
    //   return `c2c_lastCourseLocation_${classroomId}_${courseId}`;
    // } else {
    //   return `c2c_lastCourseLocation_${classroomId}`;
    // }
  }

  /**
   * Save the resource target ID from the closest ancestor .leaf-card-satcore of
   * the clicked element into the sessionStorage entry at key
   * `c2c_lastCourseElementResourceScrollPositionId_{classroomId}`.
   *
   * Flow: save the id on click; it remains in sessionStorage until the
   * course card view remounts (e.g. ClassDetails). On remount,
   * restoreCourseElementResourceScrollPosition scrolls to it and clears it.
   */
  static preserveCourseElementResourceScrollPosition = async (event, { classroomId } = {}) => {
    if (!event?.target) {
      return;
    }
    if (!userManager.isTeacher || productManager.isFromProduct) {
      return;
    }
    // Find the leaf card element from the event target
    const leafCard = event.target.closest('.leaf-card-satcore');
    if (!leafCard) {
      return;
    }
    const resourceTargetId = leafCard.getAttribute('data-element-id');
    if (!resourceTargetId) {
      return;
    }
    setSessionStorageItem(
      `c2c_lastCourseElementResourceScrollPositionId_${classroomId}`,
      resourceTargetId
    );
  }

  /**
   * Scroll to the saved resource target and clear the entry.
   * `scrollContainerSelector`: closest ancestor to scroll. Falls back to
   * `scrollIntoView` if omitted or not matched.
   */
  static restoreCourseElementResourceScrollPosition = async ({
    classroomId,
    scrollContainerSelector
  } = {}) => {
    if (productManager.isFromProduct) {
      return;
    }
    const key = `c2c_lastCourseElementResourceScrollPositionId_${classroomId}`;
    const resourceTargetId = getSessionStorageItem(key);
    if (!resourceTargetId) {
      return;
    }
    removeSessionStorageItem(key);
    // Utilize requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      try {
        // Scope leaf card lookup to the container first so we find the correct
        // one when the same data-element-id exists in multiple mounted trees
        // (e.g. Course Search modal card vs ClassDetails card behind it)
        const container = scrollContainerSelector ? document.querySelector(scrollContainerSelector) : null;
        const cardSelector = `.leaf-card-satcore[data-element-id="${resourceTargetId}"]`;
        const leafCard = container?.querySelector(cardSelector) || document.querySelector(cardSelector);
        if (leafCard) {
          if (container && container.contains(leafCard)) {
            const cardRect = leafCard.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const centerOffset = (containerRect.height - cardRect.height) / 2;
            container.scrollBy({ behavior: 'smooth', top: cardRect.top - containerRect.top - centerOffset });
          } else {
            leafCard.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          }
          // Focus after scroll animation completes
          setTimeout(() => {
            leafCard.focus?.({ preventScroll: true });
          }, 500);
        }
      } catch (e) {
        console.warn('restoreCourseElementResourceScrollPosition failed', e);
      }
    });
  }

  /**
   * Clear the saved scroll position for the given classroom. Called by view
   * unmount handlers (e.g. ClassDetails.componentWillUnmount) to avoid stale
   * restores when the user navigates away.
   */
  static clearCourseElementResourceScrollPosition = ({ classroomId } = {}) => {
    removeSessionStorageItem(`c2c_lastCourseElementResourceScrollPositionId_${classroomId}`);
  }
}

registerClass('CourseNavigationService', CourseNavigationService);
